
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface EditingLock {
  id: string;
  entity_type: string;
  entity_id: string;
  field_name: string | null;
  user_id: string;
  user_name: string;
  locked_at: string;
  expires_at: string;
}

interface UseEditingLocksConfig {
  entityType: 'card' | 'column' | 'board';
  entityId: string;
  enabled?: boolean;
}

export function useEditingLocks({ entityType, entityId, enabled = true }: UseEditingLocksConfig) {
  const { user } = useAuth();
  const [locks, setLocks] = useState<EditingLock[]>([]);

  // Fetch current locks
  useEffect(() => {
    if (!entityId || !user || !enabled) return;

    const fetchLocks = async () => {
      try {
        const { data, error } = await supabase
          .from('editing_locks')
          .select('*')
          .eq('entity_type', entityType)
          .eq('entity_id', entityId)
          .gt('expires_at', new Date().toISOString());

        if (error) {
          console.error('❌ [EDITING LOCKS] Error fetching locks:', error);
          return;
        }

        setLocks(data || []);
      } catch (error) {
        console.error('❌ [EDITING LOCKS] Unexpected error:', error);
      }
    };

    fetchLocks();

    // Subscribe to lock changes
    const channel = supabase
      .channel(`editing_locks_${entityType}_${entityId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'editing_locks',
          filter: `entity_type=eq.${entityType},entity_id=eq.${entityId}`
        },
        () => {
          fetchLocks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [entityType, entityId, user, enabled]);

  const acquireLock = async (fieldName?: string) => {
    if (!user || !enabled) return false;

    try {
      const { error } = await supabase
        .from('editing_locks')
        .upsert({
          entity_type: entityType,
          entity_id: entityId,
          field_name: fieldName || null,
          user_id: user.id,
          user_name: user.email || user.user_metadata?.full_name || 'Anonymous',
          locked_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 5 * 60 * 1000).toISOString() // 5 minutes
        });

      if (error) {
        console.error('❌ [EDITING LOCKS] Error acquiring lock:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ [EDITING LOCKS] Unexpected error:', error);
      return false;
    }
  };

  const releaseLock = async (fieldName?: string) => {
    if (!user) return;

    try {
      const query = supabase
        .from('editing_locks')
        .delete()
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .eq('user_id', user.id);

      if (fieldName) {
        query.eq('field_name', fieldName);
      } else {
        query.is('field_name', null);
      }

      const { error } = await query;

      if (error) {
        console.error('❌ [EDITING LOCKS] Error releasing lock:', error);
      }
    } catch (error) {
      console.error('❌ [EDITING LOCKS] Unexpected error:', error);
    }
  };

  const isFieldLocked = (fieldName?: string) => {
    return locks.some(lock => 
      lock.field_name === (fieldName || null) && 
      lock.user_id !== user?.id
    );
  };

  const getFieldLock = (fieldName?: string) => {
    return locks.find(lock => 
      lock.field_name === (fieldName || null) && 
      lock.user_id !== user?.id
    );
  };

  return {
    locks,
    acquireLock,
    releaseLock,
    isFieldLocked,
    getFieldLock
  };
}
